import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useClients, Client } from '../../clients/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import GenericListWidget from './GenericListWidget';

export interface RecentClientsProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const RecentClients: React.FC<RecentClientsProps> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { clients, isLoading } = useClients();

  const items = React.useMemo(() => {
    const arr = Array.isArray(clients) ? clients : [];
    return [...arr]
      .sort((a: Client, b: Client) => {
        const ta = new Date(a.clientCreatedAt || 0).getTime();
        const tb = new Date(b.clientCreatedAt || 0).getTime();
        return tb - ta;
      })
      .slice(0, effectiveLimit);
  }, [clients, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  return (
    <GenericListWidget<Client>
      title="Recent Clients"
      viewAllPath="/clients"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No clients"
      getItemKey={(c) => c.clientId}
      embedded={embedded}
      renderItem={(client) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/clients/${client.clientId}`)}
          onKeyDown={(e) =>
            e.key === 'Enter' && navigate(`/clients/${client.clientId}`)
          }
          className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm font-medium ${colors.text}`}>
              {client.clientName || `Client #${client.clientId}`}
            </p>
            <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
              {client.clientCreatedAt
                ? new Date(client.clientCreatedAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : ''}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default RecentClients;
